import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import type { RateCard } from "@rebuild/shared/models";
import type { CreateRateItemInput } from "./rateCards.types";
import OrganizationSelector from "../../components/ui/utils/OrganizationSelector";
import type { Organization } from "../../hooks/rebuildBackendFetchHooks";
import { useOrganizationSelect } from "../../hooks/useOrganizationSelect";
import MappingTopBox from "../CreatorTools/MappingTool/components/MappingTopBox";
import ExistingRateCardsTable from "./ExistingRateCardsTable";
import PdfDropZone from "./PdfDropZone";
import RateCardDetailModal from "./RateCardDetailModal";
import RateItemsReviewTable from "./RateItemsReviewTable";
import { createEmptyRateItem, isValidRateItem } from "./rateCardItems.utils";
import { createRateCard, extractRateItemsFromPdf, listRateCards } from "./rateCardsAPI";

function RateCardsPage() {
  const { organizations, selectedOrganization, setSelectedOrganization } = useOrganizationSelect({ includeTestOrgs: true });

  const [rateCards, setRateCards] = useState<RateCard[]>([]);
  const [isLoadingList, setIsLoadingList] = useState(false);
  const [selectedRateCardId, setSelectedRateCardId] = useState<string | null>(null);
  const [pendingOrgSwitch, setPendingOrgSwitch] = useState<{ org: Organization | undefined } | null>(null);

  const [name, setName] = useState("");
  const [items, setItems] = useState<CreateRateItemInput[]>([]);
  const [sourceDocPath, setSourceDocPath] = useState<string | undefined>(undefined);
  const [isExtracting, setIsExtracting] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const isDefaultOrg = selectedOrganization.organizationId === "default";
  const organizationId = selectedOrganization.organizationId;

  const loadRateCards = useCallback(async () => {
    if (isDefaultOrg) {
      setRateCards([]);
      return;
    }
    setIsLoadingList(true);
    try {
      const data = await listRateCards(organizationId);
      setRateCards(data ?? []);
    } catch (error) {
      console.error("Error loading rate cards:", error);
      toast.error("Failed to load rate cards");
    } finally {
      setIsLoadingList(false);
    }
  }, [isDefaultOrg, organizationId]);

  useEffect(() => {
    void loadRateCards();
  }, [loadRateCards]);

  const resetForm = () => {
    setName("");
    setItems([]);
    setSourceDocPath(undefined);
  };

  const handleOrgSelect = (org: Organization | undefined) => {
    if (org?.organizationId === selectedOrganization.organizationId) {
      return;
    }
    // Guard against losing unsaved draft line items when switching orgs.
    if (items.length > 0) {
      setPendingOrgSwitch({ org });
      return;
    }
    setSelectedOrganization(org);
  };

  const confirmOrgSwitch = () => {
    if (!pendingOrgSwitch) return;
    resetForm();
    setSelectedOrganization(pendingOrgSwitch.org);
    setPendingOrgSwitch(null);
  };

  const handleFileUpload = async (file: File) => {
    setIsExtracting(true);
    try {
      const result = await extractRateItemsFromPdf(organizationId, file);
      const extracted = result.items ?? [];
      setItems((current) => [...current, ...extracted]);
      setSourceDocPath(result.sourceDocPath);
      toast.success(`Extracted ${extracted.length} line items`);
    } catch (error) {
      console.error("Error extracting rate items:", error);
      const message = error instanceof Error ? error.message : "Failed to extract line items";
      toast.error(message);
    } finally {
      setIsExtracting(false);
    }
  };

  const handleItemChange = (index: number, item: CreateRateItemInput) => {
    setItems((current) => current.map((existing, i) => (i === index ? item : existing)));
  };

  const handleItemRemove = (index: number) => {
    setItems((current) => current.filter((_, i) => i !== index));
  };

  const handleAddRow = () => {
    setItems((current) => [...current, createEmptyRateItem()]);
  };

  const hasName = name.trim().length > 0;
  const hasAtLeastOneValidItem = useMemo(() => items.some(isValidRateItem), [items]);
  const allItemsValid = useMemo(() => items.every(isValidRateItem), [items]);
  const canSave = !isSaving && !isExtracting && !isDefaultOrg && hasName && hasAtLeastOneValidItem && allItemsValid;

  const handleSave = async () => {
    if (!canSave) return;
    setIsSaving(true);
    try {
      await createRateCard(organizationId, {
        name: name.trim(),
        sourceDocPath,
        items,
      });
      toast.success("Rate card created");
      resetForm();
      await loadRateCards();
    } catch (error) {
      console.error("Error creating rate card:", error);
      const message = error instanceof Error ? error.message : "Failed to create rate card";
      toast.error(message);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <MappingTopBox />
      <div className="ml-8 mt-4 pr-8 pb-10">
        <div className="mb-8">
          <h1 className="mb-2 text-3xl font-bold text-gray-800">Rate Cards</h1>
          <p className="max-w-4xl text-sm text-gray-600">
            Create a rate card by uploading a PDF. Our extractor proposes line items; you review, edit, and save.
          </p>
        </div>

        <div className="mb-6 rounded-lg bg-white p-6 shadow">
          <div className="max-w-md">
            <OrganizationSelector
              organizations={organizations}
              selectedOrganization={selectedOrganization}
              onSelect={handleOrgSelect}
              label="Organization"
            />
          </div>
        </div>

        {!isDefaultOrg && (
          <>
            <div className="mb-6 rounded-lg bg-white p-6 shadow">
              <h2 className="mb-4 text-xl font-semibold text-gray-900">Existing Rate Cards</h2>
              <ExistingRateCardsTable rateCards={rateCards} isLoading={isLoadingList} onSelect={(card) => setSelectedRateCardId(card.id)} />
            </div>

            <div className="rounded-lg bg-white p-6 shadow">
              <h2 className="mb-4 text-xl font-semibold text-gray-900">New Rate Card</h2>

              <div className="mb-4 max-w-xl">
                <label className="mb-2 block text-sm font-medium text-gray-700" htmlFor="rate-card-name">
                  Name
                </label>
                <input
                  id="rate-card-name"
                  type="text"
                  value={name}
                  onChange={(event) => setName(event.target.value)}
                  placeholder="e.g. Acme Roofing 2026 Q1"
                  className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div className="mb-6">
                <label className="mb-2 block text-sm font-medium text-gray-700">PDF Upload</label>
                <PdfDropZone onFile={handleFileUpload} isUploading={isExtracting} disabled={isSaving} />
              </div>

              <div className="mb-6">
                <div className="mb-2 flex items-center justify-between">
                  <h3 className="text-base font-semibold text-gray-900">Line Items</h3>
                  <span className="text-xs text-gray-500">
                    {items.length} item{items.length === 1 ? "" : "s"}
                  </span>
                </div>
                <RateItemsReviewTable items={items} onChange={handleItemChange} onRemove={handleItemRemove} onAddRow={handleAddRow} />
                {items.length > 0 && !allItemsValid && (
                  <p className="mt-2 text-sm text-red-600">Every row needs a description, unit, and a non-negative unit price.</p>
                )}
              </div>

              <div className="flex items-center justify-end gap-3">
                <button
                  type="button"
                  onClick={resetForm}
                  disabled={isSaving || isExtracting}
                  className="rounded-lg border border-gray-200 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  Reset
                </button>
                <button
                  type="button"
                  onClick={handleSave}
                  disabled={!canSave}
                  className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-blue-700 disabled:cursor-not-allowed disabled:bg-gray-300"
                >
                  {isSaving ? "Saving..." : "Save Rate Card"}
                </button>
              </div>
            </div>
          </>
        )}

        {isDefaultOrg && (
          <div className="rounded-lg bg-white p-6 shadow">
            <p className="text-sm text-gray-600">Select an organization above to view and create rate cards.</p>
          </div>
        )}
      </div>

      {selectedRateCardId && (
        <RateCardDetailModal
          organizationId={organizationId}
          rateCardId={selectedRateCardId}
          onClose={() => setSelectedRateCardId(null)}
          onSaved={loadRateCards}
        />
      )}

      {pendingOrgSwitch && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
          <div className="w-full max-w-md rounded-lg bg-white p-6 shadow-xl">
            <h3 className="text-lg font-semibold text-gray-900">Discard unsaved line items?</h3>
            <p className="mt-2 text-sm text-gray-600">
              You have {items.length} unsaved draft line item{items.length === 1 ? "" : "s"}. Switching organizations will clear them. This
              can't be undone.
            </p>
            <div className="mt-6 flex items-center justify-end gap-3">
              <button
                type="button"
                onClick={() => setPendingOrgSwitch(null)}
                className="rounded-lg border border-gray-200 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={confirmOrgSwitch}
                className="rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-red-700"
              >
                Discard and switch
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default RateCardsPage;
